<template>
    <div class="salary-calculator-container">
      <div class="calculator-desc">
        <h1 class="title">💰 实时工资计算器 💰</h1>
        <p class="subtitle">输入您的月薪和工作时间，实时查看您的财富增长</p>
        
        <div v-if="!isWorking" class="input-container">
          <el-row :gutter="15">
            <el-col :xs="24" :lg="12">
              <el-input-number 
                v-model="monthlySalary" 
                :min="1000" 
                :step="500" 
                controls-position="right"
                class="salary-input golden-input"
              >
                <template slot="prepend" class="golden-prepend">月薪(元)</template>
              </el-input-number>
            </el-col>
            <el-col :xs="24" :lg="12">
              <el-select v-model="workDaysPerWeek" placeholder="选择休息日" class="golden-select">
                <el-option label="双休 (每周工作5天)" :value="5"></el-option>
                <el-option label="单休 (每周工作6天)" :value="6"></el-option>
              </el-select>
            </el-col>
          </el-row>
          
          <el-row :gutter="15" class="time-row">
            <el-col :xs="24" :lg="12">
              <el-time-picker
                v-model="startTime"
                format="HH:mm"
                value-format="HH:mm"
                placeholder="上班时间"
                class="golden-time-picker"
              >
              </el-time-picker>
            </el-col>
            <el-col :xs="24" :lg="12">
              <el-time-picker
                v-model="endTime"
                format="HH:mm"
                value-format="HH:mm"
                placeholder="下班时间"
                class="golden-time-picker"
              >
              </el-time-picker>
            </el-col>
          </el-row>
          
          <el-button 
            type="primary" 
            @click="startWork" 
            :disabled="!canStart"
            class="start-button"
          >
            🚀 开始赚钱
          </el-button>
        </div>
        
        <div v-else class="salary-display">
          <div class="current-salary">
            <h2 class="salary-title">今日实时财富</h2>
            <div class="salary-amount">
              <span class="currency">¥</span><span class="animated-number">{{ animatedSalary.toFixed(2) }}</span>
            </div>
            <div class="time-info">
              <p><i class="el-icon-time"></i> 工作时间: {{ startTime }} - {{ endTime }}</p>
              <p><i class="el-icon-alarm-clock"></i> 当前时间: {{ currentTime }}</p>
              <div class="progress-container">
                <p><i class="el-icon-data-line"></i> 今日工作进度:</p>
                <el-progress 
                  :percentage="workProgress" 
                  :stroke-width="16"
                  :color="progressColor"
                  :text-inside="true"
                ></el-progress>
              </div>
            </div>
          </div>
          
          <el-button 
            type="danger" 
            @click="stopWork" 
            class="stop-button"
          >
            🏁 结束今日工作
          </el-button>
        </div>
      </div>
      
      <div class="coins-container" ref="coinsContainer"></div>
      <div class="fireworks-container" ref="fireworksContainer"></div>
    </div>
  </template>
  
  <script>
  export default {
    name: "SalaryCalculator",
    metaInfo: {
      title: "实时工资计算器",
      meta: [
        { name: "description", content: "实时计算您的工作时间对应的工资收入" }
      ]
    },
    data() {
      return {
        monthlySalary: 10000,
        workDaysPerWeek: 5,
        startTime: "09:30",
        endTime: "18:30",
        isWorking: false,
        currentSalary: 0,
        animatedSalary: 0,
        currentTime: "",
        timer: null,
        animationFrame: null,
        coinsInterval: null,
        coins: [],
        fireworks: []
      };
    },
    computed: {
      canStart() {
        return this.monthlySalary > 0 && this.startTime && this.endTime;
      },
      workProgress() {
        if (!this.startTime || !this.endTime) return 0;
        
        const start = this.timeToMinutes(this.startTime);
        const end = this.timeToMinutes(this.endTime);
        const now = this.timeToMinutes(this.currentTime);
        
        if (now < start) return 0;
        if (now > end) return 100;
        
        // 使用 parseFloat 将结果转换为 Number 类型
        return parseFloat(((now - start) / (end - start) * 100).toFixed(1));
      },
      progressColor() {
        const progress = parseFloat(this.workProgress);
        if (progress < 30) return '#ff0000';
        if (progress < 70) return '#ffcc00';
        return '#00ff00';
      },
      dailySalary() {
        const monthlyWorkDays = (365 / 7 * this.workDaysPerWeek) / 12;
        return this.monthlySalary / monthlyWorkDays;
      },
      hourlySalary() {
        const start = this.timeToMinutes(this.startTime);
        const end = this.timeToMinutes(this.endTime);
        const workHours = (end - start) / 60;
        return this.dailySalary / workHours;
      },
      minuteSalary() {
        return this.hourlySalary / 60;
      },
      secondSalary() {
        return this.minuteSalary / 60;
      }
    },
    methods: {
      timeToMinutes(timeStr) {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      },
      startWork() {
        this.isWorking = true;
        this.currentSalary = 0;
        this.animatedSalary = 0;
        this.updateCurrentTime();
        
        this.timer = setInterval(() => {
          this.updateCurrentTime();
          this.calculateSalary();
        }, 1000);
        
        this.startCoinsAnimation();
        this.startFireworks();
      },
      stopWork() {
        this.isWorking = false;
        clearInterval(this.timer);
        clearInterval(this.coinsInterval);
        cancelAnimationFrame(this.animationFrame);
        this.clearCoins();
        this.stopFireworks();
      },
      updateCurrentTime() {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        this.currentTime = `${hours}:${minutes}:${seconds}`;
      },
      calculateSalary() {
        const start = this.timeToMinutes(this.startTime);
        const end = this.timeToMinutes(this.endTime);
        const now = this.timeToMinutes(this.currentTime);
        
        if (now < start) {
          this.currentSalary = 0;
        } else if (now > end) {
          this.currentSalary = this.dailySalary;
        } else {
          const workedMinutes = now - start;
          this.currentSalary = this.hourlySalary * (workedMinutes / 60);
        }
        
        if (this.animationFrame) {
          cancelAnimationFrame(this.animationFrame);
        }
        
        this.animateSalary();
      },
      animateSalary() {
        const difference = this.currentSalary - this.animatedSalary;
        if (Math.abs(difference) < 0.01) {
          this.animatedSalary = this.currentSalary;
          return;
        }
        
        this.animatedSalary += difference * 0.1;
        this.animationFrame = requestAnimationFrame(this.animateSalary);
      },
      startCoinsAnimation() {
        this.coinsInterval = setInterval(() => {
          this.createCoin();
        }, 300);
      },
      createCoin() {
        const container = this.$refs.coinsContainer;
        if (!container) return;
        
        const coin = document.createElement('div');
        coin.className = 'coin';
        
        // 随机金币样式
        const coinTypes = ['gold', 'silver', 'bronze'];
        const coinType = coinTypes[Math.floor(Math.random() * coinTypes.length)];
        coin.classList.add(coinType);
        
        const left = Math.random() * 100;
        coin.style.left = `${left}%`;
        
        const size = Math.random() * 20 + 10;
        coin.style.width = `${size}px`;
        coin.style.height = `${size}px`;
        
        const rotation = Math.random() * 360;
        coin.style.transform = `rotate(${rotation}deg)`;
        
        const duration = Math.random() * 3 + 2;
        coin.style.animationDuration = `${duration}s`;
        
        container.appendChild(coin);
        this.coins.push(coin);
        
        setTimeout(() => {
          coin.remove();
          this.coins = this.coins.filter(c => c !== coin);
        }, duration * 1000);
      },
      clearCoins() {
        const container = this.$refs.coinsContainer;
        if (container) {
          container.innerHTML = '';
        }
        this.coins = [];
      },
      startFireworks() {
        // 简单的烟花效果
        this.fireworksInterval = setInterval(() => {
          this.createFirework();
        }, 1500);
      },
      createFirework() {
        const container = this.$refs.fireworksContainer;
        if (!container) return;
        
        const firework = document.createElement('div');
        firework.className = 'firework';
        
        const left = Math.random() * 100;
        firework.style.left = `${left}%`;
        
        const colors = ['#ff0000', '#ffcc00', '#00ff00', '#0066ff', '#ff00ff'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        firework.style.backgroundColor = color;
        
        container.appendChild(firework);
        this.fireworks.push(firework);
        
        setTimeout(() => {
          firework.remove();
          this.fireworks = this.fireworks.filter(f => f !== firework);
        }, 2000);
      },
      stopFireworks() {
        clearInterval(this.fireworksInterval);
        const container = this.$refs.fireworksContainer;
        if (container) {
          container.innerHTML = '';
        }
        this.fireworks = [];
      }
    },
    beforeDestroy() {
      this.stopWork();
    }
  };
  </script>
  
  <style scoped>
  .salary-calculator-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 160px);
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #fff9e6 0%, #ffecb3 100%);
    font-family: 'Arial Rounded MT Bold', 'Microsoft YaHei', sans-serif;
  }
  
  .calculator-desc {
    width: 90%;
    max-width: 800px;
    text-align: center;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
    border: 2px solid #ffd700;
  }
  
  .title {
    color: #d4af37;
    font-size: 2.5rem;
    margin-bottom: 10px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .subtitle {
    color: #b8860b;
    font-size: 1.2rem;
    margin-bottom: 20px;
  }
  
  .input-container {
    margin-top: 30px;
  }
  
  .time-row {
    margin: 30px 0;
  }
  
  .golden-input {
    /deep/ .el-input__inner {
      border-color: #ffd700;
      background-color: #fffdf5;
      color: #d4af37;
      font-weight: bold;
    }
    
    /deep/ .el-input-number__increase, 
    /deep/ .el-input-number__decrease {
      background-color: #ffd700;
      color: white;
      border-color: #ffd700;
    }
    
    /deep/ .el-input-group__prepend {
      background-color: #ffd700;
      color: white;
      border-color: #ffd700;
      font-weight: bold;
    }
  }
  
  .golden-select {
    /deep/ .el-input__inner {
      border-color: #ffd700;
      background-color: #fffdf5;
      color: #d4af37;
      font-weight: bold;
    }
    
    /deep/ .el-input__suffix {
      color: #ffd700;
    }
  }
  
  .golden-time-picker {
    /deep/ .el-input__inner {
      border-color: #ffd700;
      background-color: #fffdf5;
      color: #d4af37;
      font-weight: bold;
    }
    
    /deep/ .el-input__prefix, 
    /deep/ .el-input__suffix {
      color: #ffd700;
    }
  }
  
  .start-button {
    margin-top: 30px;
    width: 220px;
    font-size: 18px;
    font-weight: bold;
    background: linear-gradient(to right, #ffd700, #ff8c00);
    border: none;
    color: white;
    height: 50px;
    border-radius: 25px;
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.4);
    transition: all 0.3s;
  }
  
  .start-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 215, 0, 0.6);
    background: linear-gradient(to right, #ff8c00, #ffd700);
  }
  
  .start-button:active {
    transform: translateY(1px);
  }
  
  .start-button.is-disabled {
    background: #cccccc;
    box-shadow: none;
  }
  
  .salary-display {
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: fadeIn 0.5s;
  }
  
  .current-salary {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .salary-title {
    color: #d4af37;
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
  
  .salary-amount {
    font-size: 3.5rem;
    font-weight: bold;
    color: #ff8c00;
    margin: 30px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .currency {
    margin-right: 10px;
    color: #d4af37;
  }
  
  .animated-number {
    transition: all 0.3s ease;
    background: linear-gradient(to right, #ff8c00, #ffd700);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  .time-info {
    margin-top: 30px;
    color: #b8860b;
    font-size: 1.1rem;
  }
  
  .time-info p {
    margin: 10px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .time-info i {
    margin-right: 8px;
    color: #ff8c00;
  }
  
  .progress-container {
    margin-top: 20px;
    width: 100%;
  }
  
  .stop-button {
    margin-top: 30px;
    width: 220px;
    font-size: 18px;
    font-weight: bold;
    background: linear-gradient(to right, #ff3333, #ff6666);
    border: none;
    color: white;
    height: 50px;
    border-radius: 25px;
    box-shadow: 0 5px 15px rgba(255, 0, 0, 0.3);
    transition: all 0.3s;
  }
  
  .stop-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 0, 0, 0.4);
    background: linear-gradient(to right, #ff6666, #ff3333);
  }
  
  .stop-button:active {
    transform: translateY(1px);
  }
  
  .coins-container, .fireworks-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
  }
  
  .coin {
    position: absolute;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(255, 215, 0, 0.7);
    animation-name: coin-fall;
    animation-timing-function: cubic-bezier(0.1, 0.8, 0.3, 1);
    animation-iteration-count: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }
  
  .coin.gold {
    background: radial-gradient(circle at 30% 30%, #ffd700, #daa520);
  }
  
  .coin.silver {
    background: radial-gradient(circle at 30% 30%, #e6e6e6, #c0c0c0);
  }
  
  .coin.bronze {
    background: radial-gradient(circle at 30% 30%, #cd7f32, #8b4513);
  }
  
  .firework {
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    animation: firework-explode 2s ease-out forwards;
  }
  
  @keyframes coin-fall {
    0% {
      top: -50px;
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
    80% {
      opacity: 0.8;
    }
    100% {
      top: 100%;
      opacity: 0;
      transform: rotate(360deg) scale(0.5);
    }
  }
  
  @keyframes firework-explode {
    0% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      transform: translateY(-100px) scale(3);
      opacity: 0;
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media screen and (max-width: 768px) {
    .calculator-desc {
      width: 95%;
      padding: 20px;
    }
    
    .title {
      font-size: 2rem;
    }
    
    .subtitle {
      font-size: 1rem;
    }
    
    .salary-amount {
      font-size: 2.5rem;
    }
    
    .start-button, .stop-button {
      width: 180px;
      font-size: 16px;
      height: 45px;
    }
    
    .time-info {
      font-size: 1rem;
    }
  }
  </style>


// WEBPACK FOOTER //
// src/components/Salary.vue